/**
 * Input + Textarea + Select + Label — Sprint-16-Brand.
 *
 * Editorial-Form-Kern:
 *   - bg-white auf Cream-Page (klar getrennt vom Cream-Card-Background)
 *   - border-line + rounded-md (11px)
 *   - Focus: 2px sage-Ring inset, kein Border-Shift
 *   - Placeholder in ink-faint (muted)
 *   - Optional `leftIcon` prop — Icon links im Input mit passendem padding-left
 *
 * Native HTML statt Wrapper-Hölle. Composition via `className` und Spread.
 */
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/shared/lib/cn';

const baseInputClass = cn(
  'w-full rounded-md border border-line bg-white text-ink',
  'px-4 py-2.5 text-[14px] font-sans',
  'placeholder:text-ink-faint',
  'transition-colors duration-fast',
  'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sage focus:border-transparent',
  'disabled:opacity-40 disabled:pointer-events-none disabled:bg-card-alt',
  'aria-invalid:border-sealwax aria-invalid:ring-sealwax/20',
);

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Icon links im Input — automatic pl-10 wenn gesetzt. */
  leftIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', leftIcon, ...props }, ref) => {
    if (leftIcon) {
      return (
        <div className="relative">
          <span
            aria-hidden
            className="text-ink-faint pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
          >
            {leftIcon}
          </span>
          <input
            ref={ref}
            type={type}
            className={cn(baseInputClass, 'pl-10', className)}
            {...props}
          />
        </div>
      );
    }
    return <input ref={ref} type={type} className={cn(baseInputClass, className)} {...props} />;
  },
);
Input.displayName = 'Input';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, rows = 4, ...props }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(baseInputClass, 'min-h-22 resize-y', className)}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

/**
 * Native <select> mit Custom-Chevron als inline-SVG-data-URI (kein Radix nötig).
 * Chevron in ink-faint (#A09A8C), matched Placeholder-Ton.
 * Optional `leftIcon` prop analog Input.
 */
export interface SelectProps extends Omit<InputHTMLAttributes<HTMLSelectElement>, 'type'> {
  leftIcon?: ReactNode;
}

const selectChevron =
  "bg-[image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none' stroke='%23A09A8C' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 4.5l3 3 3-3'/%3E%3C/svg%3E\")]";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, leftIcon, ...props }, ref) => {
    const selectEl = (
      <select
        ref={ref}
        className={cn(
          baseInputClass,
          'appearance-none pr-10',
          'bg-size-[12px_12px] bg-position-[right_14px_center] bg-no-repeat',
          selectChevron,
          leftIcon && 'pl-10',
          className,
        )}
        {...props}
      >
        {children}
      </select>
    );
    if (leftIcon) {
      return (
        <div className="relative">
          <span
            aria-hidden
            className="text-ink-faint pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2"
          >
            {leftIcon}
          </span>
          {selectEl}
        </div>
      );
    }
    return selectEl;
  },
);
Select.displayName = 'Select';

/**
 * Label — Sans (Hanken) 13px medium in ink-soft. Wird über Inputs/Selects
 * gerendert und via FormField-htmlFor a11y-verlinkt.
 */
export const Label = forwardRef<HTMLLabelElement, React.LabelHTMLAttributes<HTMLLabelElement>>(
  ({ className, ...props }, ref) => (
    <label
      ref={ref}
      className={cn('text-ink-soft mb-1.5 block text-[13px] font-medium', className)}
      {...props}
    />
  ),
);
Label.displayName = 'Label';
